import { DatePipe } from '@angular/common';
import { Component, effect, input, OnDestroy, output, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TNil } from '@app/app.interface';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { endOfDay, startOfDay } from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subscription } from 'rxjs';

@Component({
  imports: [
    DatePipe,
    IconComponent,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
  ],
  providers: [provideNativeDateAdapter()],
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
})
export class DatePickerComponent implements OnDestroy {
  public readonly dateControl = input.required<FormControl<Date | null>>();

  public readonly dateLabel = input<string>();
  public readonly loading = input<boolean>(false);
  public readonly isStartOfDay = input<boolean>(false);

  public readonly dateChanged = output<Date | null>();

  public readonly date = signal<Date | null>(null);

  public dateForm = new FormGroup({
    date: new FormControl<Date | null>(null),
  });

  private readonly datePicker = viewChild.required<MatDatepicker<Date>>('datePicker');

  private lastEmittedDate: Date | null | undefined = undefined;

  private pickerSubscription: Subscription | null = null;
  private dateSubscription: Subscription | TNil = null;

  constructor() {
    effect(() => {
      if (!this.dateControl()) {
        return;
      }

      this.dateForm = new FormGroup({
        date: this.dateControl(),
      });

      this.setupPickerSubscriptions();
      this.setupFormValueChanges();
    });
  }

  public openDatePicker(event: Event): void {
    event.stopImmediatePropagation();

    setTimeout(() => this.datePicker().open());
  }

  public ngOnDestroy(): void {
    this.pickerSubscription?.unsubscribe();
    this.dateSubscription?.unsubscribe();
  }

  private setupPickerSubscriptions(): void {
    this.pickerSubscription?.unsubscribe();

    if (this.datePicker()) {
      this.pickerSubscription = this.datePicker().closedStream.subscribe(() => {
        this.emitDate();
        this.removeFloatingLabel();
      });
    }
  }

  private setupFormValueChanges(): void {
    this.dateSubscription?.unsubscribe();

    this.dateSubscription = this.dateForm.get('date')?.valueChanges.subscribe((value) => {
      const alteredDate = value ? (this.isStartOfDay() ? startOfDay(value) : endOfDay(value)) : null;
      this.dateControl().setValue(alteredDate, { emitEvent: false });
      this.date.set(alteredDate);
      this.emitDate();
    });
  }

  private emitDate(): void {
    const date = this.date();

    if (date === this.lastEmittedDate) {
      return;
    }

    this.lastEmittedDate = date;

    this.dateChanged.emit(date);
  }

  private removeFloatingLabel(): void {
    if (this.dateControl().value) {
      return;
    }

    setTimeout(() => {
      const formFields = document.querySelectorAll('app-date-picker label');

      formFields.forEach((formField) => {
        if (formField instanceof HTMLElement) {
          formField.classList.remove('mdc-floating-label--float-above');
        }
      });
    }, 130);
  }
}
